import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PaidIcon from '@mui/icons-material/Paid'
import SendIcon from '@mui/icons-material/Send'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'
import { vatMultiplierForItem, vatOptions } from '../../../helpers/region'

// Mirrors the server-side rule in routers/patientTransport/invoices.js: a
// Draft invoice can only move to Draft or Approved — everything downstream
// requires having been approved first. Only applies to transitioning an
// existing invoice's status, not the initial status on the create form.
const isStatusOptionDisabled = (currentStatus, optionStatus) =>
  (currentStatus || 'Draft') === 'Draft' && !['Draft', 'Approved'].includes(optionStatus)

const invoiceStatuses = [
  'Draft',
  'Approved',
  'Sent',
  'Partially Paid',
  'Paid',
  'Overdue',
]

const getInvoiceTotal = (invoice, region) =>
  (invoice?.items || []).reduce(
    (total, item) =>
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplierForItem(item.vat, region),
    0,
  )

const BookingInvoices = ({ booking }) => {
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { orgSettings } = orgConfig
  const region = tenantInfo?.region
  const currency = orgSettings?.currency ?? '£'
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [autoPricing, setAutoPricing] = useState(false)
  const [autoLines, setAutoLines] = useState(null) // tariff-generated items
  const [autoTariffName, setAutoTariffName] = useState('')
  const [autoVariance, setAutoVariance] = useState(null) // actuals vs accepted-quote comparison
  const [form, setForm] = useState({
    reference: '',
    invoiceDate: moment().format('YYYY-MM-DD'),
    dueDate: moment().add(30, 'days').format('YYYY-MM-DD'),
    amount: '',
    vat: 'none',
    description: '',
    status: 'Draft',
  })

  const defaultDescription = useMemo(() => {
    const patientName = [
      booking?.patient?.demographics?.firstName,
      booking?.patient?.demographics?.lastName,
    ]
      .filter(Boolean)
      .join(' ')
    return `Patient transport booking ${booking?.bookingRef || ''}${
      patientName ? ` - ${patientName}` : ''
    }`
  }, [booking])

  const loadInvoices = () => {
    if (!booking?._id) return
    setLoading(true)
    API.get(`/pts/bookings/${booking._id}/invoices`)
      .then((res) => setInvoices(res.data || []))
      .catch(() => setError('Unable to load invoices for this booking.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadInvoices()
  }, [booking?._id])

  const openCreate = () => {
    setForm({
      reference: `PTS-${booking?.bookingRef || moment().format('YYMMDD')}`,
      invoiceDate: moment().format('YYYY-MM-DD'),
      dueDate: moment().add(30, 'days').format('YYYY-MM-DD'),
      amount: '',
      vat: 'none',
      description: defaultDescription,
      status: 'Draft',
    })
    setAutoLines(null)
    setAutoTariffName('')
    setAutoVariance(null)
    setError('')
    setCreateOpen(true)
  }

  // Pulls tariff-priced line items from recorded journey data (actuals mode)
  const autoPrice = async () => {
    setAutoPricing(true)
    setError('')
    try {
      const res = await API.get(
        `/pts/bookings/${booking._id}/pricing?mode=actuals`,
      )
      if (res.status === 200) {
        if (!res.data?.lines?.length) {
          setError(
            'The tariff produced no chargeable lines — the booking may have no closed journeys yet. Enter an amount manually.',
          )
          setAutoLines(null)
          setAutoVariance(null)
        } else {
          setAutoLines(res.data.lines)
          setAutoTariffName(res.data.tariffName || '')
          setAutoVariance(res.data.variance || null)
          if (res.data.tariffWarning) {
            // Expired contract tariff fell back to the default - the person
            // approving the invoice should know which rates priced it
            setError(res.data.tariffWarning)
          }
          if (res.data.paymentTermsDays) {
            setForm((prev) => ({
              ...prev,
              dueDate: moment()
                .add(res.data.paymentTermsDays, 'days')
                .format('YYYY-MM-DD'),
            }))
          }
        }
      } else {
        setError(res.data?.error || 'Unable to auto-price this booking.')
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Unable to auto-price this booking.')
    } finally {
      setAutoPricing(false)
    }
  }

  const createInvoice = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await API.post(`/pts/bookings/${booking._id}/invoices`, {
        ...form,
        ...(autoLines?.length ? { items: autoLines } : {}),
      })
      if (res.status === 201) {
        setCreateOpen(false)
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to create invoice.')
      }
    } catch (e) {
      setError('Unable to create invoice. Please try again later.')
    } finally {
      setSaving(false)
    }
  }

  // API.js sets validateStatus: () => true, so axios never rejects on
  // 4xx/5xx — only real network failures land in catch. Every call here
  // must check res.status explicitly or a rejected request fails silently.
  const updateStatus = async (invoiceID, status) => {
    setError('')
    try {
      const res = await API.put(`/pts/invoices/${invoiceID}/status`, { status })
      if (res.status === 200) {
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to update invoice status.')
      }
    } catch (e) {
      setError('Unable to update invoice status. Please try again later.')
    }
  }

  const markPaid = async (invoiceID) => {
    setError('')
    try {
      const res = await API.post(`/pts/invoices/${invoiceID}/mark-paid`, {
        date: moment().format('YYYY-MM-DD'),
      })
      if (res.status === 200) {
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to mark invoice as paid.')
      }
    } catch (e) {
      setError('Unable to mark invoice as paid. Please try again later.')
    }
  }

  const sendInvoice = async (invoiceID) => {
    setError('')
    try {
      const res = await API.post(`/pts/invoices/${invoiceID}/send`)
      if (res.status === 200) {
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to send invoice.')
      }
    } catch (e) {
      setError('Unable to send invoice. Please try again later.')
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Create and manage invoices linked to this booking.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<ReceiptLongIcon />}
          onClick={openCreate}
        >
          Create Invoice
        </Button>
      </Stack>
      <CustomDataGrid
        rows={invoices}
        loading={loading}
        getRowId={(row) => row._id}
        autoHeight
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No invoices for this booking' }}
        columns={[
          {
            field: 'reference',
            headerName: 'Reference',
            minWidth: 140,
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 180,
            renderCell: ({ row }) => (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <TextField
                  select
                  size="small"
                  value={row.status || 'Draft'}
                  onChange={(e) => updateStatus(row._id, e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {invoiceStatuses.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      disabled={isStatusOptionDisabled(row.status, status)}
                    >
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
                {(row.status || 'Draft') === 'Draft' && (
                  <Tooltip title="A Draft invoice must be Approved before it can be marked Sent, Partially Paid, Paid, or Overdue.">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          {
            field: 'invoiceDate',
            headerName: 'Date',
            minWidth: 120,
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
          },
          {
            field: 'dueDate',
            headerName: 'Due',
            minWidth: 120,
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
          },
          {
            field: 'totalAmount',
            headerName: 'Total',
            minWidth: 110,
            renderCell: ({ row }) =>
              `${currency}${getInvoiceTotal(row, region).toFixed(2)}`,
          },
          {
            field: 'totalPaid',
            headerName: 'Paid',
            minWidth: 110,
            renderCell: ({ row }) =>
              `${currency}${Number(row.totalPaid || 0).toFixed(2)}`,
          },
          {
            field: 'actions',
            headerName: '',
            minWidth: 300,
            sortable: false,
            renderCell: ({ row }) => (
              <Stack direction="row" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  href={`/invoices/${row._id}`}
                  target="_blank"
                >
                  Open
                </Button>
                {row.status !== 'Draft' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => sendInvoice(row._id)}
                  >
                    {row.sentAt ? 'Resend' : 'Send'}
                  </Button>
                )}
                {row.status === 'Paid' ? (
                  <Chip size="small" color="success" label="Paid" />
                ) : (
                  row.status !== 'Draft' && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PaidIcon />}
                      onClick={() => markPaid(row._id)}
                    >
                      Paid
                    </Button>
                  )
                )}
              </Stack>
            ),
          },
        ]}
      />

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create PTS Invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Reference"
                    value={form.reference}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        reference: e.target.value,
                      }))
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Every invoice is born Draft - approval goes through the
                      status state machine, never the create form */}
                  <TextField label="Status" value="Draft" fullWidth disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="date"
                    label="Invoice Date"
                    value={form.invoiceDate}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        invoiceDate: e.target.value,
                      }))
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="date"
                    label="Due Date"
                    value={form.dueDate}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={autoPrice}
                    disabled={autoPricing}
                  >
                    {autoPricing
                      ? 'Pricing...'
                      : autoLines
                        ? 'Re-run auto-pricing'
                        : 'Auto-price from tariff'}
                  </Button>
                </Grid>
                {autoLines?.length ? (
                  <Grid item xs={12}>
                    <Alert
                      severity="success"
                      onClose={() => setAutoLines(null)}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {autoLines.length} line
                        {autoLines.length === 1 ? '' : 's'} from tariff
                        {autoTariffName ? ` "${autoTariffName}"` : ''} — total{' '}
                        {currency}
                        {getInvoiceTotal({ items: autoLines }, region).toFixed(2)}
                      </Typography>
                      {autoLines.map((line) => (
                        <Typography
                          key={line.id}
                          variant="caption"
                          display="block"
                        >
                          {line.quantity} × {currency}
                          {Number(line.unitPrice).toFixed(2)} —{' '}
                          {line.description}
                          {line.detail ? ` ${line.detail}` : ''}
                        </Typography>
                      ))}
                    </Alert>
                    {autoVariance && (
                      <Alert
                        severity={autoVariance.flagged ? 'warning' : 'info'}
                        sx={{ mt: 1 }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {autoVariance.varianceAmount >= 0 ? 'Over' : 'Under'}{' '}
                          quote {autoVariance.quoteRef} by {currency}
                          {Math.abs(autoVariance.varianceAmount).toFixed(2)}
                          {autoVariance.variancePct !== null
                            ? ` (${autoVariance.variancePct > 0 ? '+' : ''}${autoVariance.variancePct.toFixed(1)}%)`
                            : ''}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Quoted {currency}
                          {autoVariance.quoteSubtotal.toFixed(2)} — actual{' '}
                          {currency}
                          {autoVariance.actualsSubtotal.toFixed(2)}
                          {autoVariance.flagged
                            ? ' — exceeds the configured variance threshold, review before sending.'
                            : ''}
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label="Amount"
                        type="number"
                        value={form.amount}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        select
                        label="VAT"
                        value={form.vat}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, vat: e.target.value }))
                        }
                        fullWidth
                      >
                        {vatOptions(region).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        value={form.description}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving || !form.reference || (!form.amount && !autoLines?.length)
            }
            onClick={createInvoice}
          >
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default BookingInvoices
